/**
 * The last card: pick a theme and up to four stats, then save or share the
 * image. The preview is the real image, drawn ahead of time, so Save and Share
 * work straight from the tap.
 */

import { gsap } from "gsap";
import { Check, Download, Share } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type SyntheticEvent } from "react";

import { prefersReducedMotion } from "../motion";
import { play } from "../sound";
import { Scene, W, Zone, useColourOf, usePictureOf, useStageExtra, type BlobSpec } from "../story/stage";
import { useTimeline } from "../story/timeline";
import { THEME_IDS, shareTheme, type ThemeId } from "../theme";
import type { Period, PersonStory } from "../types";
import {
  HEIGHT,
  MAX_STATS,
  STAT_LABELS,
  WIDTH,
  availableStats,
  canShareImages,
  defaultOptions,
  downloadImage,
  drawShare,
  loadPicture,
  imageText,
  loadFonts,
  renderImage,
  shareImage,
  toggleStat,
  type ShareOptions,
} from "./drawShare";

const BLOBS: BlobSpec[] = [
  { colour: "blurple", x: 60, y: 260, r: 260, seed: 21.2, wob: 0.22 },
  { colour: "yellow", x: 1060, y: 900, r: 160, seed: 22.6, wob: 0.22 },
];

// The preview is as tall as fits between the kicker and the theme label on the 1920 stage.
const PREVIEW_TOP = 246;
const PREVIEW_H = 736;
const PREVIEW_RADIUS = 32;
/** A swatch plus the gap after it: how far the ring moves per theme. */
const RING_STEP = 124;

const THEME_NAMES: Record<ThemeId, string> = { ink: "Ink", sun: "Sun", mint: "Mint", sky: "Sky", paper: "Paper" };

// The story moves on when its card is tapped (and pauses when held); using a control must not count as either.
const stop = (event: SyntheticEvent) => event.stopPropagation();
const keep = { onPointerDown: stop, onClick: stop };

type Toast = { text: string; ok: boolean; n: number };

export function ShareCard({ story, period }: { story: PersonStory; period: Period }) {
  const [opts, setOpts] = useState<ShareOptions>(() => defaultOptions(story));
  const [image, setImage] = useState<Blob | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [canShare] = useState(canShareImages);
  const canvas = useRef<HTMLCanvasElement>(null);
  const preview = useRef<HTMLDivElement>(null);
  const flash = useRef<HTMLDivElement>(null);
  const saveButton = useRef<HTMLButtonElement>(null);
  const toastEl = useRef<HTMLDivElement>(null);
  const colour = useColourOf()(story.person.name);
  const picture = usePictureOf()(story.person.name);
  const still = prefersReducedMotion();
  // A taller stage moves the controls down by its extra height (rounded as the zone rounds it): the preview grows into that room.
  const previewH = PREVIEW_H + Math.round(useStageExtra());
  const previewW = Math.round((previewH * WIDTH) / HEIGHT);

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx) return;
    let current = true;
    setImage(null);
    // Draw once the fonts and the picture are in, then make the PNG now rather than on the tap.
    void Promise.all([loadFonts(imageText(story, period, opts)), loadPicture(picture)]).then(([, img]) => {
      if (!current) return;
      drawShare(ctx, story, period, opts, colour, img);
      return renderImage(el).then((blob) => {
        if (current) setImage(blob);
      });
    });
    return () => {
      current = false;
    };
  }, [story, period, opts, colour, picture]);

  useLayoutEffect(() => {
    if (!toast || still || !toastEl.current) return;
    gsap.fromTo(toastEl.current, { y: 60, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.45, ease: "back.out(2)", delay: 0.2 });
  }, [toast, still]);

  const show = (text: string, ok: boolean) => setToast((t) => ({ text, ok, n: (t?.n ?? 0) + 1 }));

  /** Change the image; a toast was about the old one, so it goes. */
  const change = (next: ShareOptions) => {
    if (next === opts) return;
    setToast(null);
    setOpts(next);
  };

  const root = useTimeline((s) => {
    s.rise(".kick", 0.3, { y: 0, sound: false });
    s.tl.fromTo(".sprev", { y: 700, rotation: -7, autoAlpha: 0 }, { y: 0, rotation: 0, autoAlpha: 1, duration: 0.9, ease: "back.out(1.2)" }, 0.3);
    s.sound("whoosh", 0.3);
    s.rise([...s.q("[data-part=theme-label]"), ...s.q(".sw-row"), ...s.q(".sring")], 1.0, { y: 30, stagger: 0.06 });
    s.rise([...s.q("[data-part=picks-label]"), ...s.q(".picks")], 1.15, { y: 30, stagger: 0.06 });
    s.rise([...s.q(".tgl-row"), ...s.q(".btns")], 1.3, { y: 30, stagger: 0.08 });
  });

  const save = () => {
    if (!image) return;
    downloadImage(image, period.title);
    play("shutter");
    play("ding", 1.2);
    show(`Saved · ${WIDTH}×${HEIGHT} PNG`, true);
    if (still) return;
    // The video's save: the button squeezes, the preview flashes, then gives a little wobble.
    gsap
      .timeline()
      .to(saveButton.current, { scale: 0.94, duration: 0.1, ease: "power1.in" })
      .to(saveButton.current, { scale: 1, duration: 0.3, ease: "back.out(3)" });
    gsap.fromTo(flash.current, { autoAlpha: 0.8 }, { autoAlpha: 0, duration: 0.5, ease: "power2.out", delay: 0.1 });
    gsap.to(preview.current, { rotation: 1.5, y: -10, duration: 0.6, yoyo: true, repeat: 1, ease: "sine.inOut", delay: 0.8, overwrite: "auto" });
  };

  const share = () => {
    if (!image) return;
    shareImage(image, period.title).then(
      () => {
        play("ding");
        show("Shared", true);
      },
      (error) => {
        // Closing the share sheet without picking anything is not a failure.
        if (!(error instanceof DOMException && error.name === "AbortError")) show("Couldn't share the image.", false);
      },
    );
  };

  const ease = (props: string) => (still ? undefined : props);
  const ring = THEME_IDS.indexOf(opts.theme);

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        {/* The preview stays at the top and grows down into the room the controls leave. */}
        <Zone at="top">
          <div className="kick">Make it yours</div>

          <div ref={preview} className="sprev" style={{ left: (W - previewW) / 2, top: PREVIEW_TOP, width: previewW, height: previewH }}>
            <canvas
              ref={canvas}
              width={WIDTH}
              height={HEIGHT}
              role="img"
              aria-label="Your share image"
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                borderRadius: PREVIEW_RADIUS,
                background: shareTheme(opts.theme).bg,
                boxShadow: "0 40px 100px color-mix(in srgb, var(--wr-ink) 45%, transparent)",
              }}
            />
            <div ref={flash} aria-hidden className="abs wr-hide" style={{ inset: 0, borderRadius: PREVIEW_RADIUS, background: "var(--wr-white)" }} />
          </div>
        </Zone>

        {/* The controls are the card's foot, so they keep to the bottom, near the thumb. */}
        <Zone at="bottom">
          <div data-part="theme-label" className="ctl-lab" style={{ top: 1016, textTransform: "uppercase" }}>
            Theme
          </div>
          <div className="sw-row" role="group" aria-label="Theme" {...keep}>
            {THEME_IDS.map((id) => (
              <button
                key={id}
                type="button"
                className="sw"
                aria-label={`${THEME_NAMES[id]} theme`}
                aria-pressed={opts.theme === id}
                style={{ background: shareTheme(id).bg }}
                onClick={() => change(opts.theme === id ? opts : { ...opts, theme: id })}
              />
            ))}
          </div>
          <div
            aria-hidden
            className="sring"
            style={{ left: 62 + ring * RING_STEP, pointerEvents: "none", transition: ease("left 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)") }}
          />

          <div data-part="picks-label" className="ctl-lab" style={{ top: 1196, textTransform: "uppercase" }}>
            Show up to {MAX_STATS}
          </div>
          <div className="picks" role="group" aria-label={`Show up to ${MAX_STATS}`} {...keep}>
            {availableStats(story).map((stat) => {
              const on = opts.stats.includes(stat);
              return (
                <button
                  key={stat}
                  type="button"
                  data-stat={stat}
                  aria-pressed={on}
                  className={`pick${on ? " on" : ""}`}
                  style={{ transition: ease("background-color 0.2s, color 0.2s") }}
                  onClick={() => {
                    const stats = toggleStat(opts.stats, stat);
                    change(stats === opts.stats ? opts : { ...opts, stats });
                  }}
                >
                  {STAT_LABELS[stat]}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="tgl-row"
            aria-pressed={opts.showNames}
            onPointerDown={stop}
            onClick={(event) => {
              stop(event);
              change({ ...opts, showNames: !opts.showNames });
            }}
          >
            <span>Friends' names</span>
            <span
              aria-hidden
              className="tgl"
              style={{
                background: opts.showNames ? undefined : "color-mix(in srgb, var(--wr-ink) 45%, transparent)",
                transition: ease("background-color 0.25s"),
              }}
            >
              <span className="knob" style={{ transform: `translateX(${opts.showNames ? 60 : 0}px)`, transition: ease("transform 0.25s ease-out") }} />
            </span>
          </button>

          <div className="btns" {...keep}>
            <button ref={saveButton} type="button" className="btn pri" disabled={!image} style={{ opacity: image ? 1 : 0.5 }} onClick={save}>
              <Download aria-hidden strokeWidth={2.5} />
              Save image
            </button>
            {canShare ? (
              <button type="button" className="btn sec" disabled={!image} style={{ opacity: image ? 1 : 0.5 }} onClick={share}>
                <Share aria-hidden strokeWidth={2.5} />
                Share
              </button>
            ) : null}
          </div>

          <div role="status">
            {toast ? (
              <div key={toast.n} ref={toastEl} className="toast">
                {toast.ok ? (
                  <span>
                    <Check aria-hidden size={36} strokeWidth={4} style={{ display: "inline", verticalAlign: -5, marginRight: 12 }} />
                  </span>
                ) : null}
                {toast.text}
              </div>
            ) : null}
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
